import { Page } from "../../../components/page-base";
import { AppDataGrid } from "../../../components/datagrid/AppDataGrid";
import { NameCell } from "../../../components/datagrid/NameCell";
import type {
  DataGridColumn,
  DataGridRow,
  FilterChip,
} from "../../../components/datagrid/types";
import { Container, TiltePage } from "./styles";
import { isMobile } from "is-mobile";
import { useState, useMemo, useCallback, useEffect } from "react";
import { Box, Chip, IconButton, Tooltip } from "@mui/material";
import { useNavigate } from "react-router-dom";
import EmojiEventsOutlinedIcon from "@mui/icons-material/EmojiEventsOutlined";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import UnfoldMoreIcon from "@mui/icons-material/UnfoldMore";
import { RankingCell } from "../../../components/datagrid/RankingCell";
import { RankedCell } from "../../../components/datagrid/RankedCell";
import { toast } from "react-toastify";
import { CaseDetailsDialog } from "../components/case-details-dialog";
import { CasesService } from "../services";
import { useUserStore } from "../../../store/user";
import {
  FilterDrawer,
  FilterOption,
  SearchableMultiChipFilterConfig,
  SearchFilterConfig,
} from "../../../components/filter-drawer";
import { CategoriesService } from "../../../services/categories";
import { translateCategoryName } from "../../../utils/categoryTranslations";

export interface CaseProfile {
  id: number;
  publicId: string;
  ranking: number;
  name: string;
  email: string;
  location: string;
  rankedKeywords: number;
  rankedKeywordsLabel: string;
  totalKw: number;
  successRate: number;
}

const initialFilters = {
  localidade: "",
  categories: [] as FilterOption[],
};

export const Cases = () => {
  const { user } = useUserStore();

  const [profiles, setProfiles] = useState<CaseProfile[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  const [isDetailsModalOpen, setIsDetailsModalOpen] = useState(false);
  const [selectedCase, setSelectedCase] = useState<CaseProfile | null>(null);
  const [isFilterDrawerOpen, setIsFilterDrawerOpen] = useState(false);
  const [filters, setFilters] = useState(initialFilters);
  const [debouncedLocalidade, setDebouncedLocalidade] = useState("");

  const [pagination, setPagination] = useState({
    page: 1,
    pageSize: 20,
    totalCount: 0,
  });

  type SortDirection = "asc" | "desc" | null;
  const [sortDirection, setSortDirection] = useState<SortDirection>(null);

  const handleSortRankedKeywords = useCallback(() => {
    setSortDirection((prev) =>
      prev === null ? "asc" : prev === "desc" ? "asc" : null
    );
    setPagination((p) => ({ ...p, page: 1 }));
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearchTerm(searchTerm), 400);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedLocalidade(filters.localidade), 400);
    return () => clearTimeout(timer);
  }, [filters.localidade]);

  useEffect(() => {
    const fetchCases = async () => {
      if (user) {
        setIsLoading(true);
        const categoryIds = filters.categories.map((c) => c.value);
        const res = await CasesService.getCases({
          token: user.token,
          page: pagination.page - 1,
          size: pagination.pageSize,
          ...(debouncedSearchTerm && { query: debouncedSearchTerm }),
          ...(sortDirection && { sort: `${sortDirection}` }),
          ...(debouncedLocalidade && { localidade: debouncedLocalidade }),
          ...(categoryIds.length > 0 && { categoryIds }),
        });

        if (res) {
          const mapped: CaseProfile[] = res.content.map((item: any) => ({
            id: item.position,
            publicId: item.publicId,
            ranking: item.position,
            name: item.name,
            email: "",
            location: `${item.district}, ${item.city}`,
            rankedKeywords: item.rankedKeywords,
            rankedKeywordsLabel: item.label,
            totalKw: item.totalKeywords,
            successRate: item.rankingRate,
          }));
          setProfiles(mapped);
          setPagination((p) => ({ ...p, totalCount: res.totalElements }));
        }
        setIsLoading(false);
      }
    };
    fetchCases();
  }, [user, pagination.page, pagination.pageSize, sortDirection, debouncedSearchTerm, debouncedLocalidade, filters.categories]);

  const navigate = useNavigate();

  const handlePageChange = useCallback((page: number) => {
    setPagination((p) => ({ ...p, page }));
  }, []);

  const handlePageSizeChange = useCallback((pageSize: number) => {
    setPagination((p) => ({ ...p, pageSize, page: 1 }));
  }, []);

  const handleNameClick = (profile: CaseProfile) => {
    setSelectedCase(profile);
    setIsDetailsModalOpen(true);
  };

  const handleCopyLink = (publicId: string) => {
    const link = `${window.location.origin}/dashboard/${publicId}`;
    navigator.clipboard.writeText(link);
    toast.success("Link copiado para a área de transferência!");
  };

  const handleFilterChange = (field: string, value: any) => {
    setFilters((prev) => ({ ...prev, [field]: value }));
    setPagination((p) => ({ ...p, page: 1 }));
  };

  const handleClearFilters = () => {
    setFilters(initialFilters);
    setPagination((p) => ({ ...p, page: 1 }));
  };

  const searchableMultiChipFilters: SearchableMultiChipFilterConfig[] = useMemo(
    () => [
      {
        id: "categories",
        label: "Categorias",
        placeholder: "Buscar categoria...",
        onSearch: async (query: string) => {
          if (!user?.token) return [];
          const results = await CategoriesService.searchCategories(user.token, query);
          return results.map((c) => ({
            label: translateCategoryName(c.name),
            value: c.id,
          }));
        },
      },
    ],
    [user?.token]
  );

  const searchFilters: SearchFilterConfig[] = [
    {
      id: "localidade",
      label: "Localidade",
      placeholder: "Buscar por localidade",
      onSearch: () => {},
    },
  ];

  const filterChips: FilterChip[] = useMemo(() => {
    const chips: FilterChip[] = [];

    if (filters.localidade) {
      chips.push({
        id: "localidade",
        label: `Local: ${filters.localidade}`,
        onDelete: () => handleFilterChange("localidade", ""),
      });
    }

    filters.categories.forEach((option) => {
      chips.push({
        id: `category-${option.value}`,
        label: option.label,
        onDelete: () =>
          handleFilterChange(
            "categories",
            filters.categories.filter((c) => c.value !== option.value)
          ),
      });
    });

    return chips;
  }, [filters]);

  const columns: DataGridColumn[] = [
    { id: "ranking", header: "Ranking", minWidth: 120 },
    { id: "name", header: "Nome do Negócio", minWidth: 250 },
    { id: "location", header: "Localidade", minWidth: 150 },
    {
      id: "rankedKeywords",
      minWidth: 200,
      header: (
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          Palavras Ranqueadas
          <Tooltip
            title={
              sortDirection === null
                ? "Ordenar decrescente"
                : sortDirection === "desc"
                ? "Ordenar crescente"
                : "Remover ordenação"
            }
          >
            <IconButton size="small" onClick={handleSortRankedKeywords}>
              {sortDirection === "desc" ? (
                <ArrowDownwardIcon fontSize="inherit" />
              ) : sortDirection === "asc" ? (
                <ArrowUpwardIcon fontSize="inherit" />
              ) : (
                <UnfoldMoreIcon fontSize="inherit" />
              )}
            </IconButton>
          </Tooltip>
        </Box>
      ),
    },
  ];

  const rows: DataGridRow[] = useMemo(() => {
    return profiles.map((profile) => ({
      id: profile.id,
      render: {
        ranking: <RankingCell rank={profile.ranking} />,
        name: (
          <NameCell
            primaryText={profile.name}
            secondaryText={profile.email}
            onClick={() => handleNameClick(profile)}
          />
        ),
        location: (
          <Chip
            label={profile.location}
            variant="outlined"
            size="small"
            sx={{
              backgroundColor: "#EAF1FB",
              borderColor: "#D6E4F7",
              color: "#0B4FA8",
            }}
          />
        ),
        rankedKeywords: (
          <RankedCell
            value={profile.rankedKeywords}
            label={profile.rankedKeywordsLabel}
          />
        ),
      },
    }));
  }, [profiles, navigate]);

  return (
    <Page.Root>
      <Container id="dashboard-container">
        <Page.HeaderAux />
        <Page.Header
          title={
            <TiltePage>
              <EmojiEventsOutlinedIcon
                fontSize="medium"
                sx={{ color: "#f6b704" }}
              />
              <span>Cases de Sucesso</span>
            </TiltePage>
          }
          subtitle="Perfis com excelente ranqueamento (8+ palavras-chave nas primeiras posições)"
        />
        <Box
          sx={{ width: "88%", marginBottom: isMobile() ? "6vh" : "1px", mt: 3 }}
        >
          <AppDataGrid
            columns={columns}
            rows={rows}
            tableLabel="cases"
            searchTerm={searchTerm}
            onSearchTermChange={setSearchTerm}
            searchPlaceholder="Buscar por nome ou localidade..."
            onOpenFilters={() => setIsFilterDrawerOpen(true)}
            filterChips={filterChips}
            filterCount={filterChips.length}
            isLoading={isLoading}
            pagination={{
              ...pagination,
              onPageChange: handlePageChange,
              onPageSizeChange: handlePageSizeChange,
            }}
          />
          <CaseDetailsDialog
            open={isDetailsModalOpen}
            onClose={() => setIsDetailsModalOpen(false)}
            caseData={selectedCase}
            onViewDetails={(publicId) => navigate(`/dashboard/${publicId}`)}
            onCopyLink={handleCopyLink}
          />
        </Box>
        <FilterDrawer
          open={isFilterDrawerOpen}
          onClose={() => setIsFilterDrawerOpen(false)}
          filters={filters}
          onFilterChange={handleFilterChange}
          onClearFilters={handleClearFilters}
          searchableMultiChipFilters={searchableMultiChipFilters}
          searchFilters={searchFilters}
        />
      </Container>
    </Page.Root>
  );
};
